import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { useLiveConversation } from '@/hooks/useLiveConversation';
import { MonoLabel } from '@/components/editorial';
import {
  SidebarBrand,
  SidebarProfileRow,
  SidebarRoleSwitcher,
  SidebarFooter,
  NavItemWithTooltip,
  UnreadBadge,
  type NavSection,
} from './sidebar-primitives';
import { buildTrferNavSections, TRFER_FOOTER_ITEMS } from './trfer-nav';
import { useDrawerInert } from './useIsMdScreen';
import { useUnreadCounts } from '@/hooks/useUnreadCounts';

interface TRFerSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  collapsed?: boolean;
  pinned?: boolean;
  /** Collapse / expand the pinned rail — the brand block's panel icon (2026-09-20). */
  onToggle?: () => void;
  /** Opens the profile dialog — the sidebar profile row is the only entry point (#768). */
  onProfileClick?: () => void;
}

// Impersonation menu keys, one per sidebar route. The filter below fails
// OPEN — an href missing from this map always renders in impersonated
// sessions. Because of that, every key added here is a behavior change and
// must ship together with (1) a label for the key in the registry at
// src/lib/impersonationMenus.ts (which menusForRoles() turns into the
// impersonation dialog's checkbox list, and into its all-checked default)
// and (2) the allowed_menus payload posted via useImpersonateUser in
// src/pages/admin/useAdminData.ts — otherwise the newly-keyed items vanish
// from every impersonated session (#624).
//
// '/help' is deliberately NOT mapped: it is a footer utility, read-only and
// role-agnostic, and should stay visible in every impersonated session —
// the fail-open default is the intended behavior there.
export const MENU_KEY_MAP: Record<string, string> = {
  '/trfer':              'dashboard',
  '/trfer/threads':      'threads',
  // `families` governs the Families page and all three of its views since
  // 2026-09-14 (DECISIONS "Caseload consolidation"): the roster, Needs you
  // (what `/trfer/queue` was — key `queue`) and Reports (what `/trfer/reports`
  // was — key `reports`). Both old hrefs redirect into it and are unmapped,
  // the `pipeline`/`offers` reasoning: a redirect that lands on a page the
  // grant hides is still hidden by that page's own key.
  '/trfer/families':     'families',
  // `schedule` (#1084) and `calendar` are both unmapped since 2026-09-19:
  // the Schedule page is removed (desk pages redesign, S1) and both hrefs
  // redirect to Families, so `families` governs where they land.
  '/trfer/resources':    'resources',
  '/trfer/community':    'community',
  '/trfer/reimbursements': 'reimbursements',
  '/trfer/spend':        'spend',
  '/trfer/polls':        'polls',
  // `settings` also governs the Handoff tab since 2026-09-14: Handoff config
  // is `/trfer/settings?tab=handoff`, `/trfer/handoff-config` redirects there,
  // and the `handoff-config` key is unmapped.
  '/trfer/settings':     'settings',
  // `users` governs the Users page and both its views since 2026-09-14
  // (#1980): the roster (with Pending signup as a status filter — what
  // `/trfer/signups`, key `signups`, was) and Assignments (what
  // `/trfer/families/manage`, key `families-manage`, was). Both old hrefs
  // redirect into it and are unmapped, the `pipeline`/`offers` reasoning.
  '/trfer/users':        'users',
  '/trfer/applications': 'applications',
  // `/trfer/pipeline` and `/trfer/offers` are NOT mapped since 2026-09-13:
  // both redirect into /trfer/applications, which `applications` already
  // governs. Unmapped hrefs fail OPEN, and a redirect that lands on a page
  // the grant hides is still hidden by that page's own key.
  '/trfer/grants':       'grants',
  // 2026-09-14: Grant totals split into Awards (lane A) and Balances (lane B).
  // Awards keeps the page's key — `grant-totals` is the stable identity of a
  // grant the admin already made, and keys never rename. `/trfer/grant-totals`
  // redirects to Awards and is unmapped. 2026-09-19 (grant awards wave):
  // Awards absorbed Balances, whose address now redirects here too; the
  // `balances` key is retired the way `pipeline` and `offers` were, and
  // `grant-totals` governs the one page.
  '/trfer/awards':       'grant-totals',
  '/trfer/referrals':    'referrals',
  // Family wording used to hold its own key here, on the reasoning that "they
  // are two destinations, and one persisted key governing both would leave an
  // admin unable to grant one without the other". They are ONE destination as
  // of Mario's ruling (2026-08-29) — the wording is the Forms surface's second
  // view — so the reasoning inverts: a key with no nav row cannot be granted or
  // revoked from anything, and `forms` now governs both views. That merge is a
  // real consequence of the move, and `TRFerSidebar.test.tsx` states it out
  // loud rather than leaving it to be discovered.
  '/trfer/forms':        'forms',
};

function GroupUnreadSum({ label, sum }: { label: string; sum: number }) {
  const { t } = useTranslation('shell');
  if (sum <= 0) return null;
  return (
    <div className="mb-1 flex justify-center" data-testid="group-unread-sum">
      <UnreadBadge count={sum} />
      <span className="sr-only">{t('sidebar.inGroup', { group: label })}</span>
    </div>
  );
}

export function TRFerSidebar({
  isOpen,
  onClose,
  collapsed = false,
  pinned = true,
  onToggle,
  onProfileClick,
}: TRFerSidebarProps) {
  const { user, impersonation } = useAuth();
  // #2595: closed and not a visible rail → out of the tab order.
  const inert = useDrawerInert(isOpen, pinned);

  // Escape closes the drawer (#2435). It is a hand-built overlay, not a
  // Radix dialog, so nothing else gives it the key.
  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onClose]);

  // Re-renders on a language change, so the nav rows (whose labels resolve
  // through `NAV_KEY` when read, #2109 L6) follow the locale.
  useTranslation();

  // The two unread counts (ui-ux-guideline §2.7): the shell polls one
  // endpoint and the badges hang off the hrefs. Only a TRFer session asks —
  // the endpoint is TRFer-mounted, and an admin wearing this shell through
  // impersonation would only collect a 403 a minute.
  const { counts } = useUnreadCounts({ enabled: user?.role === 'trfer' });
  // Keyed by href, so the labels can rename without touching this. Since
  // 2026-09-18 `threads` is the Unreads number — the hook fields never rename
  // (§2.8). The Threads page's `conversations` count left with the page on
  // 2026-10-23.
  const countFor: Record<string, number> = {
    '/trfer/threads': counts.threads,
    '/trfer/families': counts.families,
  };

  // The nav itself lives in `trfer-nav.ts` (#611) so the help page can derive
  // its cards from the same list instead of maintaining a second copy that
  // silently drifts every time this nav moves.
  const { enabled: liveConversation } = useLiveConversation();
  const baseSections = buildTrferNavSections({
    canAssignTrfers: user?.permissions?.can_assign_trfers === true,
    canReviewApplications: user?.permissions?.can_review_applications === true,
    showUnreads: liveConversation,
  });

  const sections: NavSection[] = impersonation
    ? baseSections
        .map((s) => ({
          ...s,
          items: s.items.filter((item) => {
            const key = MENU_KEY_MAP[item.href];
            return !key || impersonation.allowedMenus.includes(key);
          }),
        }))
        .filter((s) => s.items.length > 0)
    : baseSections;

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className={cn('fixed inset-0 bg-stevens-scrim-soft z-[60]', pinned && 'md:hidden')}
          onClick={onClose}
        />
      )}

      {/* Sidebar panel. `inert` while closed off-canvas (#2595): the slide
          hides its paint, not its focusable controls. */}
      <aside
        inert={inert}
        className={cn(
          // As a drawer it is a full-height overlay ABOVE the z-50 header
          // (#2435, the shell stacking contract of D-2398); it used to start
          // at top-12, under a header the language / machine-translation row
          // had pushed down over the drawer's brand row and close button.
          'fixed top-0 left-0 z-[60] h-[100dvh] pt-[env(safe-area-inset-top)] bg-stevens-bg-2 border-r border-stevens-rule-2 transition-all duration-300 flex flex-col',
          // Pinned at md+ it is a rail, back at z-40 below every z-50 dialog.
          pinned && 'md:z-40 md:pt-0',
          // Full height beside the content, impersonating or not: the
          // impersonation banner is a row of the content column now (#2937),
          // not a fixed strip the rail had to start under.
          pinned && 'md:top-0 md:h-screen',
          collapsed ? 'w-16' : 'w-64',
          isOpen ? 'translate-x-0' : '-translate-x-full',
          pinned && 'md:translate-x-0',
        )}
      >
        {/* Brand block — TRF mark + Tyler Robinson Foundation (#872) */}
        <SidebarBrand collapsed={collapsed} pinned={pinned} onToggle={onToggle} onClose={onClose} />

        {/* Navigation — fills available space, scrolls if it doesn't fit. */}
        <nav className="flex flex-col gap-1 p-2 flex-1 min-h-0 overflow-y-auto">
          {sections.map((section, idx) => (
            <div
              key={section.label ?? `utility-${idx}`}
              className={cn(
                // Same card frame as the parent/admin sidebar (Sidebar.tsx) so
                // the staff sidebars read as one system (#624).
                'rounded-lg border bg-card/40',
                idx > 0 && 'mt-3',
                collapsed ? 'border-border/40 p-1' : 'border-border/60 p-2',
              )}
            >
              {section.label && !collapsed && (
                <MonoLabel className="block px-1 pb-1.5 mb-1 border-b border-border/40 text-stevens-ink-3/70">
                  {section.label}
                </MonoLabel>
              )}
              {/* Collapsed: the group's sum sits where its label would, so a
                  count is never hidden behind a folded rail (§2.7). */}
              {section.label && collapsed && (
                <GroupUnreadSum
                  label={section.label}
                  sum={section.items.reduce((n, item) => n + (countFor[item.href] ?? 0), 0)}
                />
              )}
              <div className="flex flex-col gap-1">
                {section.items.map((item) => (
                  <NavItemWithTooltip
                    key={item.name}
                    href={item.href}
                    icon={item.icon}
                    name={item.name}
                    collapsed={collapsed}
                    onClose={onClose}
                    end={item.end}
                    count={countFor[item.href]}
                  />
                ))}
              </div>
            </div>
          ))}
        </nav>

        {/* Footer — Settings, Help, the profile row, then the role switcher.
            Sits at the bottom of the flex column naturally — no absolute
            positioning (that previously hid the last nav items behind the
            footer when the nav outgrew the visible sidebar height).

            Order mirrors the parent/admin sidebar (Sidebar.tsx) — Victor's
            2026-08-17 call, superseding #768's between-Settings-and-Help
            placement so the two staff shells read identically. The switcher
            renders nothing at all for the single-role accounts that are the
            majority. Legal links are no longer here — see `LegalStrip`. */}
        <div className="border-t bg-background">
          <div className="px-2 pt-2 flex flex-col gap-1">
            <NavItemWithTooltip
              href={TRFER_FOOTER_ITEMS[0].href}
              icon={TRFER_FOOTER_ITEMS[0].icon}
              name={TRFER_FOOTER_ITEMS[0].name}
              collapsed={collapsed}
              onClose={onClose}
            />
            <NavItemWithTooltip
              href={TRFER_FOOTER_ITEMS[1].href}
              icon={TRFER_FOOTER_ITEMS[1].icon}
              name={TRFER_FOOTER_ITEMS[1].name}
              collapsed={collapsed}
              onClose={onClose}
            />
          </div>

          <div className="px-2 pb-2 pt-1.5 flex flex-col gap-1">
            <SidebarProfileRow collapsed={collapsed} onProfileClick={onProfileClick} />
            <SidebarRoleSwitcher collapsed={collapsed} />
          </div>

          {/* No tagline (2026-09-18, Q11, ui-ux-guideline §2.8 rule 6): a
              sentence in the chrome is deleted. "Grant work, family by
              family." and its (?) marker went; the F-20 line before it went
              for leading with "caseload". Only the trf.org link stands. */}
          <SidebarFooter collapsed={collapsed} />
        </div>
      </aside>
    </>
  );
}
